<template>
  <v-dialog
    :value="showDialog"
    width="400px"
    :min-height="progressBar ? '150px' : '500px'"
    :loading="loading"
    persistent
    :no-actions="progressBar"
  >
    <!-- Main content -->
    <v-card>
      <v-card-title class="text-h5 grey lighten-2">
          {{$t('eds.eds')}}
        </v-card-title>
        <v-container>
      <v-row v-if="!progressBar">
        <v-col class="pb-0 pt-12">
          <!-- Sign type selection -->
          <v-select
            v-model="keyType"
            color="primary"
            :label="$t('eds.signType')"
            :items="signItems"
            item-text="label"
            item-value="value"
            return-object
            hide-details
            menu-props="offsetY"
            outlined
            dense
            :disabled="loading || privateKeyIsSaved"
            class="body-1 mb-8"
            @change="changeSignType"
          >
            <template #prepend-inner>
              <v-icon small :class="`pt-1 pr-2`">fas fa-key</v-icon>
            </template>
          </v-select>

          <!-- Key center selection -->
          <v-autocomplete
            v-if="showKeyCenterSelect"
            :items="issuerCNs"
            v-model="issuerCN"
            outlined
            dense
            hide-details
            class="body-1 mb-8"
            autocomplete="off"
            :disabled="loading || privateKeyIsSaved"
            :label="$t('eds.serviceProvider')"
            :placeholder="$t('eds.providerPlaceholder')"
          >
            <template #prepend-inner>
              <v-icon small :class="`pt-1 pr-2`">fas fa-passport</v-icon>
            </template>
          </v-autocomplete>

          <!-- File key -->
          <eds-file-key v-if="keyType.name === 'fileKey'" v-model="fileKey" :loading="loading || privateKeyIsSaved" :password.sync="password" @sign="toSign" />

          <!-- Hardware key -->
          <eds-hardware-key
            v-if="keyType.name === 'hardwareKey'"
            v-model="hardwareKey"
            :key-medias="keyMedias"
            :loading="loading || privateKeyIsSaved"
            :password.sync="password"
            @reload="reload"
            @sign="toSign"
          />

          <eds-diia 
            v-if="isDiia"
            :url="url"
            :qrCode="qrCode"
            @clearDataDiia="clearDataDiia"
            @generateNewQR="generateNewQR()"
          />
          <!-- Save key options -->
          <v-radio-group v-model="saveKeyOption" hide-details :disabled="loading" dense class="eds-save" v-if="!isDiia">
            <v-radio :label="$t('eds.notSaveKey')" value="notSave" class="align-start mb-4" />
            <v-radio :label="$t('eds.saveKeyInSession')" value="saveInSession" class="align-start mb-4" />
          </v-radio-group>
        </v-col>
      </v-row>

      <!-- Progress bar for multiple sign -->
      <v-row v-else align="center" class="pt-4">
        <v-col>
          <v-progress-linear color="primary" height="40" :value="(progressBarValue / attachments.length) * 100" striped>
            {{ progressBarValue }} / {{ attachments.length }}
          </v-progress-linear>
        </v-col>
      </v-row>
        </v-container>
      <!-- Error message -->
      <v-alert v-if="error" outlined prominent color="warning" class="mb-6" border="left">
        <div class="body-2" v-html="error.message" style="word-break: break-word"></div>
        <div v-for="(item, index) in error.urls" :key="index" class="pl-1 pt-2">
          <a :href="item.href">{{item.caption}}</a>
        </div>
        <!-- Refresh button -->
        <v-btn x-small class="mt-4" color="secondary" @click="reload">
          <span>{{ $t('eds.refresh') }}</span>
        </v-btn>
      </v-alert>

      <!-- Dialog actions -->
      <v-card-actions>
        <v-spacer />
        <!-- Cancel button -->
        <v-btn :class="`mr-2`" color="blue-grey" :disabled="loading" @click="cancelDialog">
          {{ $t('eds.cancel') }}
        </v-btn>

        <!-- Sign button -->
        <v-btn v-if="showSignButton" color="success" :disabled="isSignBtnDisabled" @click="toSign">
          {{ $t('eds.sign') }}
        </v-btn>
      </v-card-actions>
      <v-overlay :absolute="true" :value="loading" :opacity="0.2">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
import EdsFileKey from './EdsFileKey'
import EdsHardwareKey from './EdsHardwareKey'
import EdsDiia from './EdsDiia'

import eds from '../../mixins/eds'

export default {
  currentPopupController: null,
  name: 'EdsCreate',
  mixins: [eds],

  components: {
    EdsFileKey,
    EdsHardwareKey,
    EdsDiia
  },

  data: () => ({
    fileKey: null,
    hardwareKey: null,
    password: null,
    issuerCN: null,
    issuerCNs: [],
    saveKeyOption: 'notSave',
    progressBar: false,
    showDialog: false,
    attachment: {},
    attachments: [],
    multipleSign: false,
    isInitialized: false
  }),
  methods: {
    cancelDialog() {
      this.showDialog = false
      this.$options.popupController.resolve(null)

      // if the current type of key is Diia, we need to reset url and qrCode when close the dialog
      if (this.isDiia) {
        this.clearDataDiia()
      }
      this.resetKeyType()
    },
    clearDataDiia() {
      this.qrCode = null,
      this.url = null
    },
    async generateNewQR() {
      await this.toSign()
    },
    async initializeWidget() {
      if (this.privateKeyIsSaved) {
        this.toSign()
        return
      }

      this.loading = true
      try {
        await this.dsInit()
        await this.postInitActions()
      } catch (e) {
        console.warn(e)
        this.error = e
      }
      this.loading = false
      this.isInitialized = true
    },
    async signFile(argument) {
      if (!this.isInitialized) {
        await this.initializeWidget()
      }
      this.attachment = undefined
      this.attachments = undefined
      this.multipleSign = undefined

      if (Array.isArray(argument) && !ArrayBuffer.isView(argument)) {
        this.attachments = argument
        this.multipleSign = true
      } else {
        this.attachment = argument
      }
      let resolve
      let reject
      const popupPromise = new Promise((ok, fail) => {
        resolve = ok
        reject = fail
      })
      
      this.$options.popupController = { resolve, reject}
      this.showDialog = true
      // Если пароль не запомнен - чистим данные
      if (!this.privateKeyIsSaved) {
        this.password = this.fileKey = this.hardwareKey = null
      }
      return popupPromise
    },
    async changeSignType(value) {
      if (value?.name === 'diiaSignature') {
        await this.readPrivateKeyDiia(false)
        await this.toSign()
      }
    }
  },

  computed: {
    isDiia() {
      return this.keyType?.name === 'diiaSignature'
    },
    signItems() {
      return [
        {
          name: 'fileKey',
          label: this.$t('eds.fileKey'),
          value: 0
        },
        {
          name: 'hardwareKey',
          label: this.$t('eds.hardwareKey'),
          value: 1
        },
        {
          name: 'diiaSignature',
          label: 'Дія.Підпис',
          value: 2
        }
      ]
    },
    showKeyCenterSelect() {
      if (this.isDiia) {
        return false
      }
      return true
    },
    showSignButton() {
      if (this.isDiia) {
        return false
      }
      return true
    },
    isSignBtnDisabled() {
      switch (this.keyType.name) {
        case 'fileKey':
          return !this.fileKey || !this.password || this.loading
        case 'hardwareKey':
          return !this.password || this.loading
        default:
          return false
      }
    }
  },

  created() {
    this.$emit('update:loading', false)
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>
